import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { RowsChangeData } from 'react-data-grid';
import { useAuth } from '../../context/AuthContext';
import { useOrgTheme } from '../../context/OrgThemeContext';
import { Employee } from '../../types';
import { useEmployees } from '../../hooks/useEmployees';
import { API_URL } from '../../constants/appConstants';
import { ADMIN_NAMES } from '../../config/teamConfig';
import {
  buildPerformanceTeamList,
  PerformanceTeamFilter,
} from '../../utils/employeeGroup';
import { useNotifications } from '../notifications';
import { ResolveEmployeeAction } from '../../hooks/useEmployees';
import EmployeeDetailModal from '../employee-detail/EmployeeDetailModal';
import ImportProgress from '../feedback/ImportProgress';
import ErrorBanner from '../feedback/ErrorBanner';
import EmployeeGrid from '../grid/EmployeeGrid';
import EditEmployeeModal from './EditEmployeeModal';
import './PerformanceTrackerPage.css';
import '../../themes/performanceTrackerThemes.css';

interface ArchivedEmployee extends Employee {
  archived_at: string;
}

interface IntranetDeveloper {
  id: string;
  name: string;
}

function buildArchivedIntranetKeys(archived: ArchivedEmployee[]): Set<string> {
  const keys = new Set<string>();
  archived.forEach((employee) => {
    if (employee.intranet_id) {
      keys.add(String(employee.intranet_id));
    }
    keys.add(employee.name.toLowerCase().trim());
  });
  return keys;
}

function showProvisionToast(
  showToast: ReturnType<typeof useNotifications>['showToast'],
  name: string,
  action: ResolveEmployeeAction
) {
  if (action === 'existing') return;

  if (action === 'created') {
    showToast({
      type: 'success',
      title: 'Added to roster',
      message: `${name} was added to the performance tracker.`,
      duration: 5000,
    });
    return;
  }

  if (action === 'updated') {
    showToast({
      type: 'success',
      title: 'Linked to PhishingBox',
      message: `${name} was linked to the PhishingBox team in the performance tracker.`,
      duration: 5000,
    });
    return;
  }

  showToast({
    type: 'info',
    title: 'Already on roster',
    message: `${name} is already in the performance tracker.`,
    duration: 4000,
  });
}

const PerformanceTrackerPage: React.FC = () => {
  const { user } = useAuth();
  const { themeOrg, loading: orgThemeLoading } = useOrgTheme();
  const { showToast } = useNotifications();
  const {
    employees,
    loading,
    error,
    saving,
    rowHeightGetter,
    handleRowsChange,
    handleNotesChange,
    handleNotesBlur,
    syncRowHeight,
    fetchEmployees,
    addEmployee,
    deleteEmployee,
    saveAllEmployees,
    resolveEmployee,
    setError
  } = useEmployees();

  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [provisioningKey, setProvisioningKey] = useState<string | null>(null);
  const [importing, setImporting] = useState<boolean>(false);
  const [importProgress, setImportProgress] = useState<string[]>([]);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [archivedEmployees, setArchivedEmployees] = useState<ArchivedEmployee[]>([]);
  const [loadingArchived, setLoadingArchived] = useState<boolean>(false);
  const [teamFilter, setTeamFilter] = useState<PerformanceTeamFilter>('elink');
  const [hasManualTeamFilter, setHasManualTeamFilter] = useState(false);
  const [intranetDevelopers, setIntranetDevelopers] = useState<IntranetDeveloper[]>([]);
  const [archivedIntranetKeys, setArchivedIntranetKeys] = useState<Set<string>>(new Set());

  const isAdmin = user?.username 
    ? ADMIN_NAMES.some(name => name.toLowerCase() === user.username.toLowerCase())
    : false;

  useEffect(() => {
    if (!orgThemeLoading && !hasManualTeamFilter) {
      setTeamFilter(themeOrg === 'phishingbox' ? 'phishingbox' : 'elink');
    }
  }, [themeOrg, orgThemeLoading, hasManualTeamFilter]);

  useEffect(() => {
    const fetchDevelopers = async () => {
      try {
        const response = await axios.get<IntranetDeveloper[]>(`${API_URL}/intranet/developers`, {
          withCredentials: true,
        });
        setIntranetDevelopers(response.data);
      } catch (err) {
        console.error('Error fetching intranet developers:', err);
      }
    };

    fetchDevelopers();
  }, []);

  const refreshArchivedIntranetKeys = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/employees/archived`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data: ArchivedEmployee[] = await response.json();
        setArchivedIntranetKeys(buildArchivedIntranetKeys(data));
      }
    } catch (err) {
      console.error('Error fetching archived employees for PhishingBox filter:', err);
    }
  }, []);

  useEffect(() => {
    if (teamFilter === 'phishingbox') {
      refreshArchivedIntranetKeys();
    }
  }, [teamFilter, employees, refreshArchivedIntranetKeys]);

  const displayedEmployees = useMemo(
    () =>
      buildPerformanceTeamList({
        employees,
        intranetDevelopers,
        teamFilter,
        archivedIntranetKeys,
      }),
    [employees, intranetDevelopers, teamFilter, archivedIntranetKeys]
  );

  const hasUnsavedIntranetRows = useMemo(
    () => teamFilter === 'phishingbox' && displayedEmployees.some((employee) => employee.id < 0),
    [teamFilter, displayedEmployees]
  );

  const handleDisplayedRowsChange = useCallback(
    async (rows: Employee[], data: RowsChangeData<Employee>) => {
      let resolvedRows = [...rows];
      let workingEmployees = [...employees];

      for (const displayIndex of data.indexes ?? []) {
        const row = resolvedRows[displayIndex];
        if (!row || row.id > 0) continue;

        try {
          const result = await resolveEmployee(row);
          workingEmployees = workingEmployees.some((employee) => employee.id === result.employee.id)
            ? workingEmployees.map((employee) =>
                employee.id === result.employee.id ? result.employee : employee
              )
            : [...workingEmployees, result.employee];
          resolvedRows[displayIndex] = result.employee;
          showProvisionToast(showToast, result.employee.name, result.action);
        } catch (err) {
          console.error('Error provisioning employee for grid edit:', err);
          setError('Failed to add employee to the performance roster');
          return;
        }
      }

      const updatedById = new Map(resolvedRows.map((row) => [row.id, row]));
      const mergedEmployees = workingEmployees.map(
        (employee) => updatedById.get(employee.id) ?? employee
      );

      const changedIndexes = (data.indexes ?? [])
        .map((displayIndex) => {
          const row = resolvedRows[displayIndex];
          if (!row || row.id <= 0) return -1;
          return mergedEmployees.findIndex((employee) => employee.id === row.id);
        })
        .filter((index) => index >= 0);

      handleRowsChange(mergedEmployees, { ...data, indexes: changedIndexes });
    },
    [employees, handleRowsChange, resolveEmployee, setError, showToast]
  );

  const openEmployee = useCallback(
    async (row: Employee) => {
      const key = row.intranet_id ?? String(row.id);
      setProvisioningKey(key);
      try {
        const result = await resolveEmployee(row);
        showProvisionToast(showToast, result.employee.name, result.action);
        setSelectedEmployee(result.employee);
      } catch (err) {
        console.error('Error opening employee:', err);
        setError('Failed to open employee details');
      } finally {
        setProvisioningKey(null);
      }
    },
    [resolveEmployee, setError, showToast]
  );

  const editEmployee = useCallback(
    async (employee: Employee) => {
      const key = employee.intranet_id ?? String(employee.id);
      setProvisioningKey(key);
      try {
        const result = await resolveEmployee(employee);
        showProvisionToast(showToast, result.employee.name, result.action);
        setEditingEmployee(result.employee);
      } catch (err) {
        console.error('Error editing employee:', err);
        setError('Failed to open employee editor');
      } finally {
        setProvisioningKey(null);
      }
    },
    [resolveEmployee, setError, showToast]
  );

  const handleNotesChangeWithProvision = useCallback(
    async (id: number, value: string) => {
      if (id > 0) {
        handleNotesChange(id, value);
        return;
      }

      const placeholder = displayedEmployees.find((employee) => employee.id === id);
      if (!placeholder) return;

      try {
        const result = await resolveEmployee(placeholder);
        showProvisionToast(showToast, result.employee.name, result.action);
        handleNotesChange(result.employee.id, value);
      } catch (err) {
        console.error('Error provisioning employee for notes:', err);
        setError('Failed to add employee to the performance roster');
      }
    },
    [displayedEmployees, handleNotesChange, resolveEmployee, setError, showToast]
  );

  const fetchArchivedEmployees = useCallback(async () => {
    setLoadingArchived(true);
    try {
      const response = await fetch(`${API_URL}/employees/archived`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setArchivedEmployees(data);
        setArchivedIntranetKeys(buildArchivedIntranetKeys(data));
      }
    } catch (err) {
      console.error('Error fetching archived employees:', err);
    } finally {
      setLoadingArchived(false);
    }
  }, []);

  const handleDeleteRow = async (id: number) => {
    if (id <= 0) {
      showToast({
        type: 'info',
        title: 'Not on roster yet',
        message: 'This person is only listed from the intranet. Open them first to add to the performance tracker, or they will stay visible here.',
        duration: 5000,
      });
      return;
    }

    const target = displayedEmployees.find((employee) => employee.id === id);
    const confirmMessage = teamFilter === 'phishingbox'
      ? `Archive ${target?.name ?? 'this employee'}? They will be removed from the PhishingBox performance list. You can restore them later from View Archived.`
      : 'Are you sure you want to archive this employee? They can be restored later from the archived section.';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    const deleted = await deleteEmployee(id);
    if (deleted) {
      showToast({
        type: 'success',
        title: 'Employee archived',
        message: `${deleted.name} was archived successfully.`,
        duration: 5000,
      });
      await refreshArchivedIntranetKeys();
      if (showArchived) {
        fetchArchivedEmployees();
      }
    }
  };

  const handleAddEmployee = async () => {
    const created = await addEmployee(teamFilter);
    if (created) {
      showToast({
        type: 'success',
        title: 'Employee added',
        message: `New ${teamFilter === 'phishingbox' ? 'PhishingBox' : 'eLink'} employee row was created. Fill in their details and save.`,
        duration: 5000,
      });
    }
  };

  const handleRestoreEmployee = async (id: number) => {
    try {
      const response = await fetch(`${API_URL}/employees/${id}/restore`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        fetchArchivedEmployees();
        fetchEmployees();
        await refreshArchivedIntranetKeys();
        showToast({
          type: 'success',
          title: 'Employee restored',
          message: 'The employee was restored to the performance tracker.',
          duration: 5000,
        });
      }
    } catch (err) {
      console.error('Error restoring employee:', err);
    }
  };

  const handlePermanentDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to PERMANENTLY delete this employee? This action cannot be undone and all their data will be lost forever.')) {
      return;
    }
    try {
      const response = await fetch(`${API_URL}/employees/${id}/permanent`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        fetchArchivedEmployees();
      }
    } catch (err) {
      console.error('Error permanently deleting employee:', err);
    }
  };

  useEffect(() => {
    if (showArchived) {
      fetchArchivedEmployees();
    }
  }, [showArchived, fetchArchivedEmployees]);

  const handleSave = async () => {
    const success = await saveAllEmployees();
    if (success) {
      alert('Changes saved successfully!');
    }
  };

  const handleEditSave = async (updatedEmployee: Employee) => {
    const updatedEmployees = employees.map(emp =>
      emp.id === updatedEmployee.id ? updatedEmployee : emp
    );
    handleRowsChange(updatedEmployees, { indexes: [employees.findIndex(e => e.id === updatedEmployee.id)] } as any);
    setEditingEmployee(null);
    await saveAllEmployees();
  };

  const handleImport = async () => {
    if (!window.confirm('This will replace all existing employee data with data from the Excel file. Continue?')) {
      return;
    }

    setImporting(true);
    setError(null);
    setImportProgress([]);

    try {
      const eventSource = new EventSource(`${API_URL}/import`);

      eventSource.onmessage = event => {
        const data = JSON.parse(event.data);

        if (data.type === 'start') {
          setImportProgress([data.message]);
        } else if (data.type === 'progress') {
          setImportProgress(prev => [...prev, data.message]);
        } else if (data.type === 'success') {
          setImportProgress(prev => [...prev, `✓ ${data.message}`]);
        } else if (data.type === 'error') {
          setImportProgress(prev => [...prev, `✗ ${data.message}`]);
          setError(data.message);
        } else if (data.type === 'complete') {
          setImportProgress(prev => [...prev, `✓ ${data.message}`]);
          eventSource.close();
          setImporting(false);
          setTimeout(() => {
            fetchEmployees();
            setImportProgress([]);
          }, 1000);
        }
      };

      eventSource.onerror = err => {
        console.error('EventSource error:', err);
        setError('Connection error during import');
        eventSource.close();
        setImporting(false);
      };
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to import Excel file';
      setError(errorMessage);
      console.error('Error importing:', err);
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="performance-page page-loading" data-tracker-team={teamFilter}>
        <div className="spinner"></div>
        <span>Loading employee data...</span>
      </div>
    );
  }

  return (
    <div className="performance-page" data-tracker-team={teamFilter}>
      <div className="page-header">
        <h1>Performance Tracker</h1>
        <div className="page-actions">
          {isAdmin && (
            <>
              <button onClick={handleAddEmployee} className="btn btn-primary">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add Employee
              </button>
              <button onClick={handleSave} className="btn btn-success" disabled={saving}>
                {saving ? 'Saving...' : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                      <polyline points="17 21 17 13 7 13 7 21"></polyline>
                      <polyline points="7 3 7 8 15 8"></polyline>
                    </svg>
                    Save Changes
                  </>
                )}
              </button>
            </>
          )}
          <button onClick={fetchEmployees} className="btn btn-secondary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 4 23 10 17 10"></polyline>
              <polyline points="1 20 1 14 7 14"></polyline>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
            Refresh
          </button>
          {isAdmin && (
            <button 
              onClick={() => setShowArchived(!showArchived)} 
              className={`btn ${showArchived ? 'btn-warning' : 'btn-secondary'}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="21 8 21 21 3 21 3 8"></polyline>
                <rect x="1" y="3" width="22" height="5"></rect>
                <line x1="10" y1="12" x2="14" y2="12"></line>
              </svg>
              {showArchived ? 'Hide Archived' : 'View Archived'}
              {archivedEmployees.length > 0 && !showArchived && (
                <span className="badge">{archivedEmployees.length}</span>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="performance-team-filter">
        <span className="performance-team-label">Team:</span>
        <button
          type="button"
          className={`performance-team-btn elink ${teamFilter === 'elink' ? 'active' : ''}`}
          onClick={() => {
            setHasManualTeamFilter(true);
            setTeamFilter('elink');
          }}
        >
          eLink Design
        </button>
        <button
          type="button"
          className={`performance-team-btn phishingbox ${teamFilter === 'phishingbox' ? 'active' : ''}`}
          onClick={() => {
            setHasManualTeamFilter(true);
            setTeamFilter('phishingbox');
          }}
        >
          PhishingBox
        </button>
      </div>

      {hasUnsavedIntranetRows && (
        <p className="performance-team-hint">
          PhishingBox teammates from the intranet will be added to the performance roster automatically when you open or edit them.
        </p>
      )}

      {provisioningKey && (
        <p className="performance-team-hint performance-team-hint-loading">
          Setting up performance record...
        </p>
      )}

      <ErrorBanner message={error} />
      <ImportProgress messages={importProgress} />

      <EmployeeGrid
        employees={displayedEmployees}
        rowHeightGetter={rowHeightGetter}
        onRowsChange={handleDisplayedRowsChange}
        onRowClick={openEmployee}
        onDeleteRow={handleDeleteRow}
        onEditRow={editEmployee}
        onNotesChange={handleNotesChangeWithProvision}
        onNotesBlur={handleNotesBlur}
        syncRowHeight={syncRowHeight}
        isAdmin={isAdmin}
      />

      {showArchived && isAdmin && (
        <div className="archived-section">
          <h2>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
              <polyline points="21 8 21 21 3 21 3 8"></polyline>
              <rect x="1" y="3" width="22" height="5"></rect>
              <line x1="10" y1="12" x2="14" y2="12"></line>
            </svg>
            Archived Employees
          </h2>
          {loadingArchived ? (
            <div className="archived-loading">Loading archived employees...</div>
          ) : archivedEmployees.length === 0 ? (
            <div className="archived-empty">No archived employees</div>
          ) : (
            <div className="archived-list">
              {archivedEmployees.map(emp => (
                <div key={emp.id} className="archived-card">
                  <div className="archived-info">
                    <span className="archived-name">{emp.name}</span>
                    <span className="archived-meta">
                      {emp.department && <span>{emp.department}</span>}
                      {emp.position && <span>{emp.position}</span>}
                      <span className="archived-date">
                        Archived: {new Date(emp.archived_at).toLocaleDateString()}
                      </span>
                    </span>
                  </div>
                  <div className="archived-stats">
                    <span className="stat">
                      <strong>{emp.points}</strong> points
                    </span>
                    {emp.performance_score && (
                      <span className="stat">
                        <strong>{emp.performance_score.toFixed(2)}</strong> score
                      </span>
                    )}
                  </div>
                  <div className="archived-actions">
                    <button 
                      className="btn btn-sm btn-success"
                      onClick={() => handleRestoreEmployee(emp.id)}
                      title="Restore employee"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                        <polyline points="1 4 1 10 7 10"></polyline>
                        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                      </svg>
                      Restore
                    </button>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handlePermanentDelete(emp.id)}
                      title="Permanently delete"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                      Delete Forever
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedEmployee && (
        <EmployeeDetailModal
          employee={selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
          onUpdate={fetchEmployees}
        />
      )}

      {editingEmployee && (
        <EditEmployeeModal
          employee={editingEmployee}
          onClose={() => setEditingEmployee(null)}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
};

export default PerformanceTrackerPage;

